import axios from "axios";
import {API_DOMAIN} from "../constants";

const CLAIMS_URL = API_DOMAIN + "/api/v3/auth/claims";

// A claim is identified by its composite (claimType, claimValue) key — no numeric id.
function claimPath(claimType, claimValue) {
    return `${CLAIMS_URL}/${encodeURIComponent(claimType)}/${encodeURIComponent(claimValue)}`;
}

// GET /api/v3/auth/claims
// Returns [{claimType, claimValue, displayName, description, categoryId, categoryName}],
// ordered by category display order then display name.
export async function getClaims() {
    const response = await axios({
        url: CLAIMS_URL,
        method: "GET",
        headers: {
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
    });

    return response.data;
}

// GET /api/v3/auth/claims/{claimType}/{claimValue}
// Returns the list item shape + groups: [{id, name}] (the groups that grant the claim).
export async function getClaim(claimType, claimValue) {
    const response = await axios({
        url: claimPath(claimType, claimValue),
        method: "GET",
        headers: {
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
    });

    return response.data;
}

// POST /api/v3/auth/claims  (Global Admin)
// Payload: {claimType, claimValue, displayName, categoryId, description}. Returns the detail shape.
// 409 if the (claimType, claimValue) already exists, 400 if the categoryId is unknown.
export async function createClaim(payload) {
    const response = await axios({
        url: CLAIMS_URL,
        method: "POST",
        headers: {
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
        data: payload,
    });

    return response.data;
}

// PUT /api/v3/auth/claims/{claimType}/{claimValue}  (Global Admin)
// Payload: {claimType, claimValue, displayName, categoryId, description}. The key is immutable.
// Returns the detail shape. 400 if the categoryId is unknown, 404 if the claim is unknown.
export async function updateClaim(claimType, claimValue, payload) {
    const response = await axios({
        url: claimPath(claimType, claimValue),
        method: "PUT",
        headers: {
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
        data: payload,
    });

    return response.data;
}

// DELETE /api/v3/auth/claims/{claimType}/{claimValue}  (Global Admin)
// 204 on success. 409 if the claim is still granted by a group/user/user-type/BU, 404 if unknown.
export async function deleteClaim(claimType, claimValue) {
    return axios({
        url: claimPath(claimType, claimValue),
        method: "DELETE",
        headers: {
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
    });
}

// ----- claim associations (batch delta) -----
// Each takes a list of entries; each entry is a full association row + a `delete` flag
// (false = add, true = remove). AuthService validates the whole batch and applies it
// all-or-nothing (400 with the offending entries on any bad member / unresolvable login).
async function putAssociations(resource, entries) {
    const response = await axios({
        url: `${API_DOMAIN}/api/v3/auth/${resource}`,
        method: "PUT",
        headers: {
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
        data: entries,
    });
    return response.data;
}

// entries: [{claimType, claimValue, groupId, delete}]
export function setGroupClaims(entries) {
    return putAssociations("groupClaims", entries);
}

// entries: [{claimType, claimValue, userTypeId, delete}]
export function setUserTypeClaims(entries) {
    return putAssociations("userTypeClaims", entries);
}

// entries: [{claimType, claimValue, businessUnitCode, delete}]
export function setBusinessUnitClaims(entries) {
    return putAssociations("businessUnitClaims", entries);
}

// entries: [{claimType, claimValue, login, delete}]
export function setUserClaims(entries) {
    return putAssociations("userClaims", entries);
}
